import { Icon } from '@iconify/react';
import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { useProjectsViews } from '../hooks/useProjectsViews';
import { useAuth } from '../contexts/AuthContext';
import { usePlatform } from '../hooks/usePlatform';
import { PLANBASE_CONFIG } from '../config';
import { BannerCarousel } from '../components/BannerCarousel';
import { ProjectCard } from '../components/ProjectCard';
import { Button } from '../components/ui/button';

// ─── Drawing type image card ──────────────────────────────────────────────────
function DrawingTypeCard({ image, onClick, isLocked = false }: { image: any; onClick: () => void; isLocked?: boolean }) {
  return (
    <div
      className={`relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer group ${isLocked ? 'opacity-60' : ''}`}
      onClick={isLocked ? undefined : onClick}
    >
      <img
        src={image.url}
        alt={image.title}
        loading="lazy"
        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${isLocked ? 'blur-sm' : ''}`}
      />
      {isLocked ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <Icon icon="hugeicons:lock" className="h-6 w-6 text-white" />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-white text-sm font-medium line-clamp-1">{image.title}</p>
            {image.projectTitle && <p className="text-white/80 text-xs">{image.projectTitle}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="border border-border rounded-2xl overflow-hidden">
      <div className="aspect-[4/3] bg-muted animate-pulse" />
      <div className="p-3 space-y-2">
        <div className="h-3.5 bg-muted rounded animate-pulse" />
        <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
      </div>
    </div>
  );
}

// ─── Filter pills ─────────────────────────────────────────────────────────────
function FilterPills({ options, value, onChange }: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={scrollRef} className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
      <button
        onClick={() => onChange('all')}
        className={`px-3 py-1.5 text-xs rounded-full font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
          value === 'all' ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
        }`}
      >
        All
      </button>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 text-xs rounded-full font-medium whitespace-nowrap transition-colors capitalize flex-shrink-0 ${
            value === opt.value ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Discover() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = usePlatform();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDrawingType, setSelectedDrawingType] = useState('all');
  const [activeTab, setActiveTab] = useState<'projects' | 'drawings'>('projects');

  const userPlan = user?.metadata?.plan || 'free';
  const freeUserLimit = PLANBASE_CONFIG.planLimits.free.canViewDiscovery;

  const { projects, loading, error } = useProjects({
    category: selectedCategory === 'all' ? undefined : selectedCategory,
    status: 'published',
    limit: 100,
  });

  const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  const { viewsMap } = useProjectsViews(projectIds);

  const filteredProjects = projects.filter((project) => {
    if (project.data.isLocked) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      project.data.title?.toLowerCase().includes(q) ||
      project.data.architect?.toLowerCase().includes(q) ||
      project.data.location?.toLowerCase().includes(q) ||
      project.data.description?.toLowerCase().includes(q)
    );
  });

  const drawingTypeImages = projects.flatMap((project) => {
    if (project.data.isLocked || !project.data.images) return [];
    return project.data.images
      .filter((img: any) => typeof img === 'object' && img.title)
      .map((img: any) => ({
        ...img,
        projectId: project.id,
        projectSlug: project.slug,
        projectTitle: project.data.title,
        drawingType: detectDrawingType(img.title),
      }));
  });

  const filteredDrawingImages = selectedDrawingType === 'all'
    ? drawingTypeImages
    : drawingTypeImages.filter((img) => img.drawingType === selectedDrawingType);

  const categoryOptions = PLANBASE_CONFIG.categories.map((c) => ({ value: c.value, label: c.label }));
  const drawingTypeOptions = PLANBASE_CONFIG.drawingTypes;

  const showUpgradePrompt = userPlan === 'free' && filteredProjects.length > freeUserLimit;

  return (
    <div className="space-y-3 md:space-y-4">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      {isMobile ? (
        /* Mobile sticky header */
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm -mx-3 px-3 pt-3 pb-2 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Discover</h1>
            <button
              onClick={() => navigate('/cms-upload')}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-500 text-white active:scale-95 transition-transform"
              aria-label="Upload project"
            >
              <Icon icon="hugeicons:plus-sign" className="h-4 w-4" />
            </button>
          </div>
          <div className="relative mb-2">
            <input
              type="search"
              inputMode="search"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 h-9 border border-input rounded-full bg-muted text-foreground text-sm focus:outline-none focus:border-orange-500"
            />
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <FilterPills options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} />
          {/* Tab switcher — mobile only */}
          <div className="flex gap-0 mt-2 border-b border-border -mx-3 px-3">
            <button
              onClick={() => setActiveTab('projects')}
              className={`flex-1 pb-2 text-xs font-medium transition-colors ${
                activeTab === 'projects'
                  ? 'text-orange-500 border-b-2 border-orange-500'
                  : 'text-muted-foreground'
              }`}
            >
              Projects
            </button>
            <button
              onClick={() => setActiveTab('drawings')}
              className={`flex-1 pb-2 text-xs font-medium transition-colors ${
                activeTab === 'drawings'
                  ? 'text-orange-500 border-b-2 border-orange-500'
                  : 'text-muted-foreground'
              }`}
            >
              Drawings
            </button>
          </div>
        </div>
      ) : (
        /* Desktop header */
        <div className="flex items-center justify-between">
          <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Discover</h1>
          <Button onClick={() => navigate('/cms-upload')} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Icon icon="hugeicons:plus-sign" className="h-4 w-4 mr-1.5" />
            Upload Project
          </Button>
        </div>
      )}
      <BannerCarousel />

      {/* ── Projects panel ──────────────────────────────────────────────── */}
      {(!isMobile || activeTab === 'projects') && (
      <div className="md:bg-white md:dark:bg-[#0a0a0a] md:rounded-2xl md:border md:border-border overflow-hidden">

        {/* Desktop sticky panel header */}
        {!isMobile && (
          <div className="sticky top-0 z-10 bg-[#f0f0f0] dark:bg-muted/40 border-b border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <FilterPills options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} />
              <div className="relative w-56 flex-shrink-0">
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 h-8 border border-input rounded-full bg-muted text-foreground text-xs focus:outline-none focus:border-orange-500"
                />
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="md:p-4">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-sm">{error}</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <Icon icon="hugeicons:search-01" className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No projects found. Try adjusting your search or filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
              {filteredProjects.map((project, index) => {
                const isLocked = userPlan === 'free' && index >= freeUserLimit;
                return (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onClick={() => navigate(`/project/${project.slug || project.id}`)}
                    isLocked={isLocked}
                    viewCount={viewsMap[project.id] || 0}
                  />
                );
              })}
            </div>
          )}

          {showUpgradePrompt && (
            <div className="text-center py-8 mt-4 bg-orange-50 dark:bg-orange-950/30 rounded-xl">
              <Icon icon="hugeicons:lock" className="h-7 w-7 text-orange-500 mx-auto mb-2" />
              <h3 className="text-sm font-medium text-foreground mb-1">Unlock More Projects</h3>
              <p className="text-xs text-muted-foreground">Upgrade to Pro to view unlimited projects</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── Drawing types panel ─────────────────────────────────────────── */}
      {(!isMobile || activeTab === 'drawings') && (
      <div className="md:bg-white md:dark:bg-[#0a0a0a] md:rounded-2xl md:border md:border-border overflow-hidden">

        <div className="md:sticky md:top-0 z-10 md:bg-[#f0f0f0] md:dark:bg-muted/40 md:border-b md:border-border px-0 md:px-4 py-2 md:py-3">
          <div className="flex items-center gap-3">
            {!isMobile && <span className="text-xs font-medium text-foreground flex-shrink-0">Drawing Type</span>}
            <FilterPills options={drawingTypeOptions} value={selectedDrawingType} onChange={setSelectedDrawingType} />
          </div>
        </div>

        <div className="md:p-4">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-xl bg-muted animate-pulse" />
              ))}
            </div>
          ) : filteredDrawingImages.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {filteredDrawingImages.map((image, index) => {
                const isLocked = userPlan === 'free' && index >= freeUserLimit;
                return (
                  <DrawingTypeCard
                    key={`${image.projectId}-${index}`}
                    image={image}
                    onClick={() => navigate(`/project/${image.projectSlug || image.projectId}`)}
                    isLocked={isLocked}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-sm">No drawings found.</p>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function detectDrawingType(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('floor plan') || t.includes('floorplan')) return 'floor-plan';
  if (t.includes('roof plan') || t.includes('roofplan')) return 'roof-plan';
  if (t.includes('site plan') || t.includes('siteplan')) return 'site-plan';
  if (t.includes('elevation')) return 'elevation';
  if (t.includes('section')) return 'section';
  if (t.includes('perspective') || t.includes('3d') || t.includes('render')) return 'perspective';
  if (t.includes('mechanical') || t.includes('hvac') || t.includes('plumbing') || t.includes('electrical')) return 'mechanical-drawing';
  return 'others';
}
